import React from 'react';
import { useAppSelector } from 'flow/hooks/useRedux';
import { Map } from 'immutable';

import GenericSmallIconButton from 'flow/components/buttons/GenericSmallIconButton';
import ContactBox from 'flow/components/contact-card/ContactBoxTW';
import ContactInfoFields from 'flow/components/contact-card/ContactInfoFields';
import ContactInfoFieldsFallback from 'flow/components/contact-card/ContactInfoFieldsFallback';
import { useModal } from 'flow/contexts/ModalProvider';
import useAppContext from 'flow/hooks/useAppContext';
import {
    selectCallRecordContact,
    selectCallRecordFromKey,
} from 'flow/selectors/call-records-selectors';
import { getContact } from 'flow/selectors/contact-selectors';
import { selectUserCountryCode } from 'flow/selectors/ExtensionSelectors';
import { Contact } from 'flow/types/Contact';
import { CallRecordKey, ContactKey, ExtensionKey } from 'flow/types/EntityKeys';

import { TypedMap } from 'flow/types/TypedMap';
import { isThirdPartyContactKey } from 'flow/utils';
import { getCountryCodeFromCallRecord } from 'flow/utils/call-records-utils';

interface Props {
    contactKey: ContactKey;
    contact: TypedMap<Contact>;
    extensionKey: ExtensionKey;
    callRecordKey?: CallRecordKey;
    onNavigation?: () => void;
    inactiveBgColor?: boolean;
}

export const ContactInfoCard = ({
    contactKey,
    contact,
    extensionKey,
    callRecordKey,
    onNavigation,
    inactiveBgColor,
}: Props) => {
    const { i18next } = useAppContext();
    const { openModal } = useModal();

    const callRecord = useAppSelector(
        (state) => selectCallRecordFromKey(state, callRecordKey) || Map(),
    );

    const ownCountryCode = useAppSelector(selectUserCountryCode);

    const extensionKeyContact = useAppSelector((state) =>
        getContact(state, contactKey, extensionKey),
    );

    const callRecordContact = useAppSelector((state) =>
        selectCallRecordContact(state, callRecordKey),
    );

    const contactToUse = contact || extensionKeyContact || callRecordContact;

    if (!contactToUse) {
        return null;
    }

    const handleEditContact = () => {
        onNavigation?.();

        openModal('editContact', {
            contact: contactToUse,
        });
    };

    const handleCreateContact = () => {
        openModal('createContact', {
            contact,
            countryCode:
                getCountryCodeFromCallRecord(callRecord) || ownCountryCode,
        });
    };

    const isContactSaved =
        contactToUse.has('key') &&
        !isThirdPartyContactKey(contactToUse.get('key'));

    const contactInfoFieldsProps = {
        contact: contactToUse,
        extensionKey,
    };

    const getSetModalButton = () => {
        if (isContactSaved && contactToUse.get('editable')) {
            return (
                <GenericSmallIconButton
                    onClick={handleEditContact}
                    iconKey={'edit'}
                    label={i18next.t('Tooltip.Nav.Title.Edit')}
                    className="bg-transparent text-gray-500"
                    size="small"
                />
            );
        }

        if (!isContactSaved) {
            return (
                <GenericSmallIconButton
                    onClick={handleCreateContact}
                    iconKey={'add'}
                    label={i18next.t('Colleagues.Create.External.Title')}
                    className="bg-white text-gray-400"
                    size="small"
                />
            );
        }
    };

    return (
        <ContactBox
            headerTitle={i18next.t('Settings.Account.Contact')}
            setModalButton={getSetModalButton()}
            inactiveBgColor={inactiveBgColor}
        >
            {contactToUse.has('editableContactDTO') ? (
                <ContactInfoFields
                    {...contactInfoFieldsProps}
                    inactiveBgColor={inactiveBgColor}
                />
            ) : (
                <ContactInfoFieldsFallback {...contactInfoFieldsProps} />
            )}
        </ContactBox>
    );
};

export default ContactInfoCard;
